import { useState, useEffect, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import WizardStepper from './WizardStepper';
import WizardNavigation from './WizardNavigation';
import WelcomeStep from './steps/WelcomeStep';
import BusinessAndBrandStep, {
  BusinessAndBrandStepRef,
} from './steps/BusinessAndBrandStep';
import AddStoreStep from './steps/AddStoreStep';
import { getAllBrandsApiV2, getStoresApi } from '../../../utils/apis';
import { useUser } from '../../../hooks/useUser';

export interface BusinessData {
  brands: any[];
  stores: any[];
}

const STORAGE_KEY = 'onboarding_business_data';
const STORAGE_STEP_KEY = 'onboarding_business_step';

const steps = [
  { label: 'Welcome', emoji: '👋' },
  { label: 'Business & Brand', emoji: '🏢' },
  { label: 'Add Outlet', emoji: '🏪' },
];

const BusinessSetupWizard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useUser();
  const businessAndBrandRef = useRef<BusinessAndBrandStepRef>(null);

  // Initialize state from localStorage if available
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_STEP_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [businessData, setBusinessData] = useState<BusinessData>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved
      ? JSON.parse(saved)
      : {
          brands: [],
          stores: [],
        };
  });

  const [isStepValid, setIsStepValid] = useState(false);

  // Completed steps are derived from actual data: only show a step complete when
  // the user has met its requirement (at least 1 brand for step 1, at least 1 store for step 2)
  const completedSteps = useMemo(() => {
    const set = new Set<number>();
    if (currentStep >= 1) set.add(0);
    if (currentStep >= 2 && (businessData.brands?.length ?? 0) >= 1) set.add(1);
    if (currentStep >= 2 && (businessData.stores?.length ?? 0) >= 1) set.add(2);
    return set;
  }, [currentStep, businessData.brands?.length, businessData.stores?.length]);

  // Normalize step on load: if we're past a step but don't have required data, go back
  useEffect(() => {
    if (currentStep >= 3) {
      setCurrentStep(2);
      return;
    }
    if (currentStep >= 2 && (businessData.brands?.length ?? 0) < 1) {
      setCurrentStep(1);
      return;
    }
    if (currentStep >= 2 && (businessData.stores?.length ?? 0) < 1) {
      setCurrentStep(2);
    }
  }, []); // run once on mount

  // Persist to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(businessData));
  }, [businessData]);

  useEffect(() => {
    localStorage.setItem(STORAGE_STEP_KEY, currentStep.toString());
  }, [currentStep]);

  const handleNext = async () => {
    // If on step 1 (Business & Brand), save business data first; require at least 1 brand
    if (currentStep === 1) {
      try {
        await businessAndBrandRef.current?.saveBusinessData();
      } catch (error) {
        console.error(
          '[BusinessSetupWizard] Error saving business data:',
          error
        );
        return;
      }
      // Refetch brands so parent state and stepper reflect new data
      try {
        const brandsResponse = await getAllBrandsApiV2(user?.tenantName || '');
        const brands = brandsResponse?.data?.data || [];
        if (brands.length < 1) return;
        setBusinessData(prev => ({ ...prev, brands }));
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ ...businessData, brands })
        );
      } catch {
        return;
      }
    }

    // Add Outlet step: fetch stores from API (source of truth) and require at least 1 before proceeding
    if (currentStep === 2) {
      try {
        const [brandsResponse, storesResponse] = await Promise.all([
          getAllBrandsApiV2(user?.tenantName || ''),
          getStoresApi(user?.tenantName || ''),
        ]);

        const brands = brandsResponse?.data?.data || [];
        const stores = storesResponse?.data?.stores || [];

        if ((stores?.length ?? 0) < 1) {
          return; // Don't proceed without at least one outlet
        }

        const updatedBusinessData = {
          brands,
          stores,
        };

        setBusinessData(updatedBusinessData);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedBusinessData));
      } catch (error) {
        console.error(
          '[BusinessSetupWizard] Error fetching business data:',
          error
        );
        return;
      }

      localStorage.setItem('onboarding_business_complete', 'true');
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      await queryClient.invalidateQueries({ queryKey: ['restaurants'] });
      navigate('/');
    } else {
      // Move to next step
      console.log(
        '[BusinessSetupWizard] Moving to next step:',
        currentStep + 1
      );
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const updateBusinessData = (updates: Partial<BusinessData>) => {
    setBusinessData(prev => ({
      ...prev,
      ...updates,
    }));
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeStep onValidationChange={setIsStepValid} />;
      case 1:
        return (
          <BusinessAndBrandStep
            ref={businessAndBrandRef}
            onValidationChange={setIsStepValid}
          />
        );
      case 2:
        return (
          <AddStoreStep
            stores={businessData.stores}
            onStoresChange={stores => updateBusinessData({ stores })}
            onValidationChange={setIsStepValid}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        bgcolor: 'secondary.main',
      }}
    >
      {/* Top: Stepper */}
      <WizardStepper
        currentStep={currentStep}
        completedSteps={completedSteps}
        steps={steps}
      />

      {/* Middle: Step Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {renderCurrentStep()}
      </Box>

      {/* Bottom: Navigation - Finish on last step (Add Store), Next otherwise */}
      <WizardNavigation
        currentStep={currentStep}
        onBack={handleBack}
        onNext={handleNext}
        canProceed={isStepValid}
        showBack={currentStep > 0}
        nextButtonLabel={currentStep === 2 ? 'Finish' : 'Next'}
      />
    </Box>
  );
};

export default BusinessSetupWizard;
